import { standardizeAddressForBigC } from '@/services/Standardizers/standardizeAddressForBigC'
import { standardizeOrderFromResponse } from '@/services/Standardizers/standardizeOrderFromResponse'

import { log, error } from '@/services/Log'
import type { StandardOrder, StandardShipment } from '@/types/ShopFront/CheckoutStandards'
import type { BCCheckoutType } from '@/types/BigCommerce/BCCheckout'
import allPromisesWithRetries from '@/helpers/allPromisesWithRetries'
//---------------------------------------------------------------

export const preparePayloadToRecoverAvailableShippingOptions = ({
  order,
  shipmentId,
}: {
  order: StandardOrder,
  shipmentId: string,
}) => {
  const shipment = { ...order?.shipments.find(({ id }) => id === shipmentId) }
  if (!shipment || !shipment.shippingAddress || !shipment?.lineItems) {
    throw Error(`Trying to update shipment that is not found in passed Order\n${JSON.stringify({ shipment, shipmentId, order })}`)
  }
  shipment.shippingAddress.street1 += ' '
  const payload = {
    shippingAddress: standardizeAddressForBigC({
      ...shipment?.shippingAddress || {},
    }),
    lineItems: shipment?.lineItems.map(({ id: itemId, quantity }) => ({
      itemId,
      quantity,
    })),
  }
  const url = `/api/storefront/checkouts/${order?.cart?.id}/consignments/${shipmentId}?include=consignments.availableShippingOptions`
  return { payload, url }
}

export const digestResponseFromAddressUpdate = async ({
  status, bigcResponse, shipmentId, order,
}: {
  status: number, bigcResponse: BCCheckoutType, shipmentId: string, order: StandardOrder,
}) => {
  const newOrder = await standardizeOrderFromResponse({ order, bigcResponse })
  const shipment = newOrder?.shipments.find(({ id }) => id === shipmentId)
  const success = status === 200 && !!shipment?.availableShippingOptions.length
  return { success, newOrder, shipment }
}

// Tweaks the address to force BigCommerce to return a valid shipping quote
type ShippingOptionrecover = (input: {
  order: StandardOrder
  shipmentId: string,
  attempts?: number
  retries?: number
}) => Promise<{
  success: false,
} | {
  success: true,
  newOrder: StandardOrder,
  shipment: StandardShipment,
}>
export const recoverAvailableShippingOptions: ShippingOptionrecover = async ({
  order, shipmentId, attempts = 0, retries = 3,
}) => {
  // should only be called when bigC cache fails and available shipping options is empty
  log('recoverAvailableShippingOptions START', {
    order, shipmentId, attempts, retries,
  })
  const attempt = attempts + 1
  const { payload, url } = preparePayloadToRecoverAvailableShippingOptions({ order, shipmentId })
  try {
    const [{ default: Axios }] = await allPromisesWithRetries(() => [import('axios')])
    const { data: bigcResponse, status } = await Axios.put<BCCheckoutType>(
      url,
      payload,
    )
    const { success, newOrder, shipment } = await digestResponseFromAddressUpdate({
      status, bigcResponse, shipmentId, order,
    })
    if (!success && attempt < retries) {
      return await recoverAvailableShippingOptions({ order, shipmentId, attempts: attempt })
    } if (!success || !shipment) {
      throw new Error(`Failed to update consignment with Address ${JSON.stringify({ url, payload })}`)
    }
    log('recoverAvailableShippingOptions END', { success, newOrder, shipment })
    return { success, newOrder, shipment }
  } catch (err) {
    error('recoverAvailableShippingOptions ERROR', err)
  }
  return { success: false }
}
